import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, Clock, Save } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { ErrorState } from '@/components/shared/ErrorState'
import {
  useAcquireRegulatoryBriefLock,
  useAdminRegulatoryBrief,
  useHeartbeatRegulatoryBriefLock,
  useReleaseRegulatoryBriefLock,
  useUpdateRegulatoryBrief,
  type AdminRegulatoryBrief,
  type RegulatoryBriefCoverageState,
  type RegulatoryBriefSavePayload,
} from '@/hooks/useAdminRegulatoryBriefs'
import { formatDate } from '@/lib/utils'

type FormState = {
  scopeSummary: string
  applicabilityJson: string
  keyObligationsJson: string
  penaltyStructureJson: string
  recentAmendmentsJson: string
  coverageRationale: string
  crosswalkJson: string
  analystPublicNotes: string
  analystInternalNotes: string
  citationLogJson: string
  needsReview: boolean
  coverageState: RegulatoryBriefCoverageState
  // KAN-271 detail content.
  inForceDate: string
  supervisoryAuthority: string
  geographicScope: string
  keyAnnexesRefsJson: string
}

function formatJson(value: unknown) {
  return JSON.stringify(value ?? null, null, 2)
}

function toForm(brief: AdminRegulatoryBrief): FormState {
  return {
    scopeSummary: brief.scopeSummary,
    applicabilityJson: formatJson(brief.applicability),
    keyObligationsJson: formatJson(brief.keyObligations),
    penaltyStructureJson: formatJson(brief.penaltyStructure),
    recentAmendmentsJson: formatJson(brief.recentAmendments),
    coverageRationale: brief.coverageRationale,
    crosswalkJson: formatJson(brief.crosswalk),
    analystPublicNotes: brief.analystPublicNotes,
    analystInternalNotes: brief.analystInternalNotes,
    citationLogJson: formatJson(brief.citationLog),
    needsReview: brief.needsReview,
    coverageState: brief.coverageState,
    inForceDate: brief.inForceDate ?? '',
    supervisoryAuthority: brief.supervisoryAuthority,
    geographicScope: brief.geographicScope,
    keyAnnexesRefsJson: formatJson(brief.keyAnnexesRefs),
  }
}

function parseJsonField(label: string, raw: string) {
  try {
    return JSON.parse(raw)
  } catch {
    throw new Error(`${label} must be valid JSON.`)
  }
}

function buildSavePayload(brief: AdminRegulatoryBrief, form: FormState, expectedVersion: number): RegulatoryBriefSavePayload {
  const payload: RegulatoryBriefSavePayload = { expected_version: expectedVersion }
  if (form.scopeSummary !== brief.scopeSummary) payload.scope_summary = form.scopeSummary
  if (form.applicabilityJson !== formatJson(brief.applicability)) {
    payload.applicability = parseJsonField('Applicability', form.applicabilityJson)
  }
  if (form.keyObligationsJson !== formatJson(brief.keyObligations)) {
    payload.key_obligations = parseJsonField('Key obligations', form.keyObligationsJson)
  }
  if (form.penaltyStructureJson !== formatJson(brief.penaltyStructure)) {
    payload.penalty_structure = parseJsonField('Penalty structure', form.penaltyStructureJson)
  }
  if (form.recentAmendmentsJson !== formatJson(brief.recentAmendments)) {
    payload.recent_amendments = parseJsonField('Recent amendments', form.recentAmendmentsJson)
  }
  if (form.coverageRationale !== brief.coverageRationale) payload.coverage_rationale = form.coverageRationale
  if (form.crosswalkJson !== formatJson(brief.crosswalk)) payload.crosswalk = parseJsonField('Crosswalk', form.crosswalkJson)
  if (form.analystPublicNotes !== brief.analystPublicNotes) payload.analyst_public_notes = form.analystPublicNotes
  if (form.analystInternalNotes !== brief.analystInternalNotes) payload.analyst_internal_notes = form.analystInternalNotes
  if (form.citationLogJson !== formatJson(brief.citationLog)) {
    payload.citation_log = parseJsonField('Citation log', form.citationLogJson)
  }
  if (form.needsReview !== brief.needsReview) payload.needs_review = form.needsReview
  if (form.coverageState !== brief.coverageState) payload.coverage_state = form.coverageState
  // KAN-271 detail content. in_force_date is a nullable DB date; the PATCH
  // schema accepts strings only, so an emptied field is not sent (clearing the
  // date is out of scope for this slice).
  if (form.inForceDate !== '' && form.inForceDate !== (brief.inForceDate ?? '')) {
    payload.in_force_date = form.inForceDate
  }
  if (form.supervisoryAuthority !== brief.supervisoryAuthority) {
    payload.supervisory_authority = form.supervisoryAuthority
  }
  if (form.geographicScope !== brief.geographicScope) payload.geographic_scope = form.geographicScope
  if (form.keyAnnexesRefsJson !== formatJson(brief.keyAnnexesRefs)) {
    payload.key_annexes_refs = parseJsonField('Key annexes references', form.keyAnnexesRefsJson)
  }
  return payload
}

function hasFormChanges(brief: AdminRegulatoryBrief, form: FormState) {
  return (
    form.scopeSummary !== brief.scopeSummary ||
    form.applicabilityJson !== formatJson(brief.applicability) ||
    form.keyObligationsJson !== formatJson(brief.keyObligations) ||
    form.penaltyStructureJson !== formatJson(brief.penaltyStructure) ||
    form.recentAmendmentsJson !== formatJson(brief.recentAmendments) ||
    form.coverageRationale !== brief.coverageRationale ||
    form.crosswalkJson !== formatJson(brief.crosswalk) ||
    form.analystPublicNotes !== brief.analystPublicNotes ||
    form.analystInternalNotes !== brief.analystInternalNotes ||
    form.citationLogJson !== formatJson(brief.citationLog) ||
    form.needsReview !== brief.needsReview ||
    form.coverageState !== brief.coverageState ||
    (form.inForceDate !== '' && form.inForceDate !== (brief.inForceDate ?? '')) ||
    form.supervisoryAuthority !== brief.supervisoryAuthority ||
    form.geographicScope !== brief.geographicScope ||
    form.keyAnnexesRefsJson !== formatJson(brief.keyAnnexesRefs)
  )
}

export function AdminRegulatoryBriefEditPage() {
  const { id } = useParams<{ id: string }>()
  const detail = useAdminRegulatoryBrief(id)
  const { mutateAsync: acquireBriefLock } = useAcquireRegulatoryBriefLock(id ?? '')
  const { mutateAsync: heartbeatBriefLock } = useHeartbeatRegulatoryBriefLock(id ?? '')
  const { mutateAsync: updateBrief, isPending: isSaving } = useUpdateRegulatoryBrief(id ?? '')
  const { mutate: releaseBriefLock } = useReleaseRegulatoryBriefLock(id ?? '')

  const [form, setForm] = useState<FormState | null>(null)
  const [expectedVersion, setExpectedVersion] = useState<number | null>(null)
  const [lockStatus, setLockStatus] = useState<'pending' | 'held' | 'failed'>('pending')
  const [message, setMessage] = useState<string | null>(null)
  const lockAttemptedRef = useRef(false)
  const lockHeldRef = useRef(false)
  const heartbeatRef = useRef(heartbeatBriefLock)
  const releaseRef = useRef(releaseBriefLock)

  const brief = detail.data?.brief

  useEffect(() => {
    heartbeatRef.current = heartbeatBriefLock
  }, [heartbeatBriefLock])

  useEffect(() => {
    releaseRef.current = releaseBriefLock
  }, [releaseBriefLock])

  useEffect(() => {
    if (brief && !form) {
      setForm(toForm(brief))
      setExpectedVersion(brief.version)
    }
  }, [brief, form])

  useEffect(() => {
    if (!id || !brief || lockAttemptedRef.current) return
    lockAttemptedRef.current = true
    acquireBriefLock()
      .then((lock) => {
        setExpectedVersion(lock.version)
        lockHeldRef.current = true
        setLockStatus('held')
      })
      .catch((err) => {
        setLockStatus('failed')
        setMessage(err instanceof Error ? err.message : 'Could not acquire edit lock.')
      })
  }, [acquireBriefLock, brief, id])

  useEffect(() => {
    if (!id || lockStatus !== 'held') return undefined
    const timer = window.setInterval(() => {
      heartbeatRef.current().catch(() => {
        setLockStatus('failed')
        lockHeldRef.current = false
        setMessage('Edit lock heartbeat failed. Reload before saving.')
      })
    }, 60_000)
    return () => window.clearInterval(timer)
  }, [id, lockStatus])

  useEffect(() => {
    return () => {
      if (lockHeldRef.current && id) {
        releaseRef.current()
        lockHeldRef.current = false
      }
    }
  }, [id])

  const hasChanges = useMemo(() => {
    if (!brief || !form) return false
    return hasFormChanges(brief, form)
  }, [brief, form])

  if (detail.isError) {
    return (
      <main className="mx-auto max-w-4xl p-6">
        <ErrorState title="Could not load regulatory brief" message="Retry or return to the briefs list." onRetry={() => detail.refetch()} />
      </main>
    )
  }

  if (detail.isLoading || !brief || !form) {
    return (
      <main className="mx-auto flex max-w-6xl flex-col gap-4 p-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96 w-full" />
      </main>
    )
  }

  const save = async () => {
    if (!id || !expectedVersion) return
    setMessage(null)
    let payload: RegulatoryBriefSavePayload
    try {
      payload = buildSavePayload(brief, form, expectedVersion)
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Invalid brief content.')
      return
    }
    if (Object.keys(payload).length <= 1) return
    try {
      const saved = await updateBrief(payload)
      if (typeof saved.version === 'number') setExpectedVersion(saved.version)
      setMessage('Brief saved.')
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Save failed.')
    }
  }

  return (
    <main className="mx-auto flex max-w-6xl flex-col gap-6 p-4 md:p-6">
      <Link to="/admin/regulatory-briefs" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
        Back to regulatory briefs
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{brief.frameworkName} brief</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {brief.frameworkSlug} · {brief.jurisdiction} · Last drafted {formatDate(brief.lastDraftedAt)}
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Badge variant={brief.status === 'draft' ? 'outline' : 'default'}>{brief.status}</Badge>
            <Badge variant={brief.coverageState === 'full' ? 'default' : 'secondary'}>{brief.coverageState}</Badge>
            {brief.needsReview && <Badge variant="destructive">Needs review</Badge>}
          </div>
        </div>
        <div className="rounded-md border px-3 py-2 text-xs text-muted-foreground">
          <div className="flex items-center gap-1 font-medium text-foreground">
            <Clock className="h-3.5 w-3.5" aria-hidden />
            {lockStatus === 'held' ? 'Edit lock held' : lockStatus === 'failed' ? 'Lock unavailable' : 'Acquiring lock…'}
          </div>
          {expectedVersion && <div className="mt-1">Expected version {expectedVersion}</div>}
        </div>
      </header>

      {message && (
        <div role="alert" className="rounded-md border bg-muted/40 p-3 text-sm">
          {message}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <section className="flex flex-col gap-4 rounded-lg border p-4">
          <div>
            <h2 className="text-lg font-semibold">Generated draft</h2>
            <p className="text-sm text-muted-foreground">Pipeline output for analyst comparison.</p>
          </div>
          <ReadOnlyBlock title="Scope summary" value={brief.scopeSummary} />
          <ReadOnlyBlock title="Applicability" value={formatJson(brief.applicability)} monospace />
          <ReadOnlyBlock title="Key obligations" value={formatJson(brief.keyObligations)} monospace />
          <ReadOnlyBlock title="Penalty structure" value={formatJson(brief.penaltyStructure)} monospace />
          <ReadOnlyBlock title="Recent amendments" value={formatJson(brief.recentAmendments)} monospace />
          <ReadOnlyBlock title="Coverage rationale" value={brief.coverageRationale} />
          <ReadOnlyBlock title="Crosswalk" value={formatJson(brief.crosswalk)} monospace />
          <ReadOnlyBlock title="Citation log" value={formatJson(brief.citationLog)} monospace />
          <ReadOnlyBlock title="In force date" value={brief.inForceDate ?? ''} />
          <ReadOnlyBlock title="Supervisory authority" value={brief.supervisoryAuthority} />
          <ReadOnlyBlock title="Geographic scope" value={brief.geographicScope} />
          <ReadOnlyBlock title="Key annexes references" value={formatJson(brief.keyAnnexesRefs)} monospace />
        </section>

        <section className="flex flex-col gap-4 rounded-lg border p-4">
          <div>
            <h2 className="text-lg font-semibold">Analyst edits</h2>
            <p className="text-sm text-muted-foreground">Save updates with optimistic concurrency and the active edit lock.</p>
          </div>
          <TextAreaField label="Scope summary" value={form.scopeSummary} onChange={(scopeSummary) => setForm({ ...form, scopeSummary })} />
          <TextAreaField label="Applicability JSON" value={form.applicabilityJson} onChange={(applicabilityJson) => setForm({ ...form, applicabilityJson })} monospace />
          <TextAreaField label="Key obligations JSON" value={form.keyObligationsJson} onChange={(keyObligationsJson) => setForm({ ...form, keyObligationsJson })} monospace />
          <TextAreaField label="Penalty structure JSON" value={form.penaltyStructureJson} onChange={(penaltyStructureJson) => setForm({ ...form, penaltyStructureJson })} monospace />
          <TextAreaField label="Recent amendments JSON" value={form.recentAmendmentsJson} onChange={(recentAmendmentsJson) => setForm({ ...form, recentAmendmentsJson })} monospace />
          <TextAreaField label="Coverage rationale" value={form.coverageRationale} onChange={(coverageRationale) => setForm({ ...form, coverageRationale })} />
          <TextAreaField label="Crosswalk JSON" value={form.crosswalkJson} onChange={(crosswalkJson) => setForm({ ...form, crosswalkJson })} monospace />
          <TextAreaField label="Analyst public notes" value={form.analystPublicNotes} onChange={(analystPublicNotes) => setForm({ ...form, analystPublicNotes })} />
          <TextAreaField label="Analyst internal notes" value={form.analystInternalNotes} onChange={(analystInternalNotes) => setForm({ ...form, analystInternalNotes })} />
          <TextAreaField label="Citation log JSON" value={form.citationLogJson} onChange={(citationLogJson) => setForm({ ...form, citationLogJson })} monospace />
          <DateField label="In force date" value={form.inForceDate} onChange={(inForceDate) => setForm({ ...form, inForceDate })} />
          <TextAreaField label="Supervisory authority" value={form.supervisoryAuthority} onChange={(supervisoryAuthority) => setForm({ ...form, supervisoryAuthority })} />
          <TextAreaField label="Geographic scope" value={form.geographicScope} onChange={(geographicScope) => setForm({ ...form, geographicScope })} />
          <TextAreaField label="Key annexes references JSON" value={form.keyAnnexesRefsJson} onChange={(keyAnnexesRefsJson) => setForm({ ...form, keyAnnexesRefsJson })} monospace />
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">Coverage state</span>
            <select
              aria-label="Coverage state"
              value={form.coverageState}
              onChange={(event) => setForm({ ...form, coverageState: event.target.value as RegulatoryBriefCoverageState })}
              className="rounded-md border border-border bg-background px-3 py-2 text-sm"
            >
              <option value="partial">partial</option>
              <option value="full">full</option>
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.needsReview}
              onChange={(event) => setForm({ ...form, needsReview: event.target.checked })}
            />
            Needs review
          </label>
          <div className="flex justify-end border-t pt-4">
            <Button type="button" onClick={save} disabled={isSaving || lockStatus !== 'held' || !hasChanges}>
              <Save className="h-4 w-4" aria-hidden />
              Save brief
            </Button>
          </div>
        </section>
      </div>
    </main>
  )
}

function ReadOnlyBlock({ title, value, monospace = false }: { title: string; value: string; monospace?: boolean }) {
  return (
    <div className="rounded-md bg-muted/30 p-3">
      <h3 className="text-sm font-medium">{title}</h3>
      <pre className={`mt-2 whitespace-pre-wrap text-xs text-muted-foreground ${monospace ? 'font-mono' : 'font-sans'}`}>{value || '—'}</pre>
    </div>
  )
}

function TextAreaField({
  label,
  value,
  onChange,
  monospace = false,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  monospace?: boolean
}) {
  const id = `reg-brief-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Textarea
        id={id}
        aria-label={label}
        rows={5}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={monospace ? 'font-mono' : undefined}
      />
    </div>
  )
}

function DateField({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (value: string) => void
}) {
  const id = `reg-brief-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="date"
        aria-label={label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  )
}
